/**
 * useCollapseState Hook
 *
 * Manages collapse/expand state for schedule entity rows:
 * - Auto-collapse when multiple entities are filtered
 * - Auto-expand when single entity is filtered
 * - Toggle individual entity collapse state
 */

import { useState, useEffect, useCallback } from "react";
import type { IServiceEmployee } from "@/services/types";

export interface UseCollapseStateProps {
  /** Active entities to track collapse state for */
  activeEntities: IServiceEmployee[];
}

export interface UseCollapseStateResult {
  /** Set of collapsed entity IDs */
  collapsedEmployees: Set<number>;
  /** Toggle collapse state for a single entity */
  toggleEmployeeCollapse: (employeeId: number) => void;
}

/**
 * Hook to manage collapse/expand state for schedule rows
 *
 * Behavior:
 * - When multiple entities are filtered: collapse all rows
 * - When single entity is filtered: expand all rows
 * - Manual toggle overrides auto behavior
 */
export function useCollapseState({
  activeEntities,
}: UseCollapseStateProps): UseCollapseStateResult {
  const [collapsedEmployees, setCollapsedEmployees] = useState<Set<number>>(
    new Set(),
  );

  // Auto-collapse/expand based on entity count
  useEffect(() => {
    if (activeEntities.length > 1) {
      // Multiple entities: collapse all
      setCollapsedEmployees((prev) => {
        const allCollapsed = new Set(activeEntities.map((entity) => entity.id));
        const alreadyCollapsed =
          prev.size === allCollapsed.size &&
          [...allCollapsed].every((id) => prev.has(id));

        return alreadyCollapsed ? prev : allCollapsed;
      });
      return;
    }

    // Single or no entities: expand all
    setCollapsedEmployees((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      return new Set();
    });
  }, [activeEntities]);

  // Toggle individual entity collapse state
  const toggleEmployeeCollapse = useCallback((employeeId: number) => {
    setCollapsedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  return {
    collapsedEmployees,
    toggleEmployeeCollapse,
  };
}
